import { useState, useEffect, useRef } from 'react';
import { Play } from 'lucide-react';

interface VideoThumbnailProps {
  videoUrl: string;
  thumbnailUrl?: string | null;
  title?: string | null;
  onThumbnailClick?: () => void;
}

/**
 * VideoThumbnail component that generates a thumbnail from the video if none is provided
 * Similar to how vidstr handles video thumbnails
 */
export function VideoThumbnail({ videoUrl, thumbnailUrl, title, onThumbnailClick }: VideoThumbnailProps) {
  const [generatedThumbnail, setGeneratedThumbnail] = useState<string | null>(null);
  const [thumbnailError, setThumbnailError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    // If we have a thumbnail URL and it's not errored, don't generate one
    if (thumbnailUrl && !thumbnailError) {
      return;
    }

    // Generate thumbnail from video
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    const handleLoadedData = () => {
      try {
        // Seek to 1 second (or 10% of duration, whichever is less)
        const seekTime = Math.min(1, video.duration * 0.1);
        video.currentTime = seekTime;
      } catch (error) {
        console.error('Error seeking video for thumbnail:', error);
      }
    };

    const handleSeeked = () => {
      try {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas dimensions to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw the current frame
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert to data URL
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        setGeneratedThumbnail(dataUrl);
      } catch (error) {
        console.error('Error generating thumbnail:', error);
      }
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('seeked', handleSeeked);

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, [videoUrl, thumbnailUrl, thumbnailError]);

  // Determine which thumbnail to use
  const displayThumbnail = thumbnailError ? generatedThumbnail : (thumbnailUrl || generatedThumbnail);

  return (
    <div className="relative cursor-pointer" onClick={onThumbnailClick}>
      {displayThumbnail ? (
        <div className="relative">
          <img
            src={displayThumbnail}
            alt={title || 'Video thumbnail'}
            className="w-full h-64 object-cover transform group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            onError={() => {
              if (!thumbnailError && thumbnailUrl) {
                // If the provided thumbnail failed, try generating one
                setThumbnailError(true);
              }
            }}
          />
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
            <div className="bg-primary/90 rounded-full p-4 group-hover:scale-110 transition-transform">
              <Play className="h-8 w-8 text-primary-foreground fill-current" />
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full h-64 bg-muted flex items-center justify-center">
          <div className="bg-primary rounded-full p-4">
            <Play className="h-8 w-8 text-primary-foreground fill-current" />
          </div>
        </div>
      )}

      {/* Hidden video and canvas for thumbnail generation */}
      <video
        ref={videoRef}
        src={videoUrl}
        className="hidden"
        crossOrigin="anonymous"
        preload="metadata"
        muted
      />
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
